<template>
  <div class="private-layout">

    <!-- Sidebar -->
    <v-navigation-drawer
      v-model="drawer"
      app
      :temporary="isMobile"
      :stateless="!isMobile"
      width="256"
      color="#1E1E2D"
      dark
      class="sidebar"
      floating
    >
      <template v-slot:prepend>
        <router-link to="/home" class="sidebar-brand" aria-label="Início">
          <img :src="logoUrl" alt="Brizola e Japur" class="sidebar-brand__logo" />
          <span class="sidebar-brand__caption">Backoffice</span>
        </router-link>
      </template>

      <v-list tag="nav" dense color="transparent" class="sidebar-nav" aria-label="Menu principal">
        <template v-for="(entry, index) in visibleMenu">
          <div v-if="entry.section" :key="`section-${index}`" class="sidebar-section">{{ entry.section }}</div>
          <MenuGroup v-else-if="entry.children" :key="entry.name" :group="entry" :items="entry.allowed" />
          <MenuItem v-else :key="entry.name" :item="entry" />
        </template>
      </v-list>

      <template v-slot:append>
        <div class="sidebar-user">
          <div class="sidebar-user__avatar" aria-hidden="true">{{ userInitials }}</div>
          <div class="sidebar-user__info">
            <div class="sidebar-user__name">{{ user ? user.name : "Usuário" }}</div>
            <div class="sidebar-user__email">{{ user ? user.email : "" }}</div>
          </div>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small class="sidebar-user__logout" aria-label="Sair" v-bind="attrs" v-on="on" @click="logout">
                <v-icon size="18">mdi-logout</v-icon>
              </v-btn>
            </template>
            <span>Sair</span>
          </v-tooltip>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Barra superior: recolher o menu e breadcrumb -->
    <v-app-bar app flat height="64" color="#FFFFFF" class="topbar">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon small aria-label="Mostrar ou ocultar o menu" v-bind="attrs" v-on="on" @click.stop="drawer = !drawer">
            <v-icon size="20">mdi-dock-left</v-icon>
          </v-btn>
        </template>
        <span>{{ drawer ? "Ocultar menu" : "Mostrar menu" }}</span>
      </v-tooltip>
      <span class="topbar__divider" aria-hidden="true"></span>
      <nav class="topbar__crumbs" aria-label="Você está em">
        <template v-for="(crumb, index) in breadcrumb">
          <v-icon v-if="index > 0" :key="`sep-${index}`" size="16" class="topbar__sep">mdi-chevron-right</v-icon>
          <router-link v-if="crumb.to && index < breadcrumb.length - 1" :key="`c-${index}`" :to="crumb.to" class="topbar__crumb">{{ crumb.text }}</router-link>
          <span v-else :key="`c-${index}`" class="topbar__crumb" :class="{ 'topbar__crumb--current': index === breadcrumb.length - 1 }">{{ crumb.text }}</span>
        </template>
      </nav>
    </v-app-bar>

    <!-- Main Content -->
    <v-main class="main-content">
      <router-view></router-view>
    </v-main>

    <AIChatFab />

  </div>
</template>

<script>
import { eventHub } from "@/main";
import router from '../router';
import MenuItem from "@/layouts/components/MenuItem";
import MenuGroup from "@/layouts/components/MenuGroup.vue";
import AIChatFab from "@/components/AIChatFab.vue";
import sidebarMenu from "@/common/data/sidebar-menu.data"
import access from "@/common/access";

export default {
    name: "PrivateLayout",

    components: {
        MenuItem,
        MenuGroup,
        AIChatFab
    },

    data() {
        return {
            // Marca em contorno, na cor creme (DM-4B5DF73D).
            logoUrl: require('@/assets/logo-bej-sidebar.png'),
            drawer: true,
            user: null,
            sidebarMenu: sidebarMenu,
        };
    },

    computed: {
        // Menu ja filtrado pela permissao: grupos sem item permitido e
        // secoes sem nada abaixo delas nao aparecem.
        visibleMenu() {
            const entries = [];
            this.sidebarMenu.forEach((entry) => {
                if (entry.section) {
                    entries.push(entry);
                } else if (entry.children) {
                    const allowed = this.allowedItems(entry);
                    if (allowed.length) entries.push({ ...entry, allowed });
                } else if (this.isAllowed(entry)) {
                    entries.push(entry);
                }
            });
            return entries.filter((entry, index) => !entry.section || (entries[index + 1] && !entries[index + 1].section));
        },

        // Breadcrumb montado a partir do menu: Inicio > Grupo > Tela > Detalhe.
        breadcrumb() {
            const path = this.$route.path;
            const crumbs = [{ text: "Início", to: "/home" }];
            if (path === "/home") return crumbs;
            let found = null;
            this.sidebarMenu.forEach((entry) => {
                const items = entry.children ? entry.children.filter((item) => item.route).map((item) => ({ item, group: entry })) : entry.route ? [{ item: entry, group: null }] : [];
                items.forEach(({ item, group }) => {
                    const matches = path === item.route || path.startsWith(`${item.route}/`);
                    if (matches && (!found || item.route.length > found.item.route.length)) found = { item, group };
                });
            });
            if (!found) return crumbs;
            // Grupo com o mesmo nome da tela (ex.: Credores > Credores) nao se repete.
            if (found.group && found.group.name !== found.item.name) crumbs.push({ text: found.group.name });
            crumbs.push({ text: found.item.name, to: found.item.route });
            if (path !== found.item.route) {
                crumbs.push({ text: /\/(novo|incluir)$/.test(path) ? "Novo" : "Detalhes" });
            }
            return crumbs;
        },

        userInitials() {
            const name = (this.user && this.user.name) || "";
            const parts = name.trim().split(/\s+/).filter(Boolean);
            if (!parts.length) return "U";
            return ((parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
        },

        isMobile() {
            return this.$vuetify.breakpoint.mdAndDown;
        },
    },

    watch: {
        isMobile(val) {
            this.drawer = val ? false : this.savedDrawerState();
        },
        drawer(open) {
            if (this.isMobile) return;
            try {
                localStorage.setItem("bej.sidebar.open", open ? "1" : "0");
            } catch (error) {
                // sem armazenamento local o menu so nao lembra o estado
            }
        },
    },

    created() {
        if (this.$root.user) {
            this.user = this.$root.user.data || this.$root.user;
        }

        this.drawer = this.isMobile ? false : this.savedDrawerState();

        // Perfil de acesso para o menu (a tela inicial nao passa pelo guard que carrega).
        access.load().catch(() => {});

        eventHub.$on("logout", () => {
            this.logout();
        });

        eventHub.$on("close-menu", () => {
            if (this.isMobile) {
                this.drawer = false;
            }
        });
    },

    destroyed() {
        eventHub.$off("logout");
        eventHub.$off("close-menu");
    },

    methods: {
        savedDrawerState() {
            try {
                return localStorage.getItem("bej.sidebar.open") !== "0";
            } catch (error) {
                return true;
            }
        },

        // Item sem permissao definida aparece sempre.
        isAllowed(item) {
            return !item.path || this.$check(item.path, "GET");
        },

        // Itens permitidos do grupo; subtitulo ({ header }) so fica se tiver item abaixo.
        allowedItems(group) {
            const list = group.children.filter((item) => item.header || this.isAllowed(item));
            return list.filter((item, index) => !item.header || (list[index + 1] && !list[index + 1].header));
        },

        async logout() {
            localStorage.clear();
            access.reset();
            router.push("/login");
        },
    },
};
</script>

<style scoped lang="scss">
.private-layout {
    height: 100%;
}

.sidebar {
    border-right: none !important;
    border-radius: 0 !important;
}

.sidebar-brand {
    display: flex;
    align-items: center;
    height: 64px;
    padding: 0 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    text-decoration: none;
}

.sidebar-brand__caption {
    margin-left: 12px;
    padding-left: 12px;
    border-left: 1px solid rgba(255, 255, 255, 0.14);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.5);
}

.sidebar-brand__logo {
    display: block;
    height: 30px;
    width: auto;
}

.sidebar-nav {
    padding: 12px 12px 16px !important;
}

.sidebar-section {
    padding: 16px 10px 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.38);
}

.sidebar-user {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.sidebar-user__avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
}

.sidebar-user__info {
    flex: 1;
    min-width: 0;
}

.sidebar-user__name,
.sidebar-user__email {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 1.35;
}

.sidebar-user__name {
    font-size: 13px;
    font-weight: 500;
    color: #ffffff;
}

.sidebar-user__email {
    font-size: 11.5px;
    color: rgba(255, 255, 255, 0.45);
}

.sidebar-user__logout .v-icon {
    color: rgba(255, 255, 255, 0.5) !important;
}

.sidebar-user__logout:hover .v-icon {
    color: #ffffff !important;
}

.topbar {
    border-bottom: 1px solid #e2e8f0 !important;

    ::v-deep .v-toolbar__content {
        padding: 0 20px;
        gap: 4px;
    }
}

.topbar__divider {
    width: 1px;
    height: 18px;
    margin: 0 10px 0 8px;
    background: #e2e8f0;
}

.topbar__crumbs {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    font-size: 14px;
}

.topbar__crumb {
    color: #64748b !important;
    text-decoration: none;
    white-space: nowrap;
}

a.topbar__crumb:hover {
    color: #0f172a !important;
}

.topbar__crumb--current {
    color: #0f172a !important;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
}

.topbar__sep {
    color: #94a3b8 !important;
}

// No celular fica so a tela atual
@media (max-width: 600px) {
    .topbar__crumb:not(.topbar__crumb--current),
    .topbar__sep {
        display: none;
    }
}

.main-content {
    background-color: #F6F7F9 !important;
}
</style>
